import { useState } from 'react';
import { Card, CardTitle, CardDescription } from '@/components/ui/card';
import { ScoreTag } from '@/components/common/ScoreTag';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { FeedFilters } from '@/components/feed/FeedFilters';
import { ExpandableArticle } from '@/components/feed/ExpandableArticle';
import { useFeed, useFeedSourceTypes } from '@/lib/queries';
import { fmtLocalDate, truncate } from '@/lib/format';
import type { FeedRowDto } from '@/types/dto';

export default function Feed() {
  const [filters, setFilters] = useState({ source: '', minScore: 0, days: 7 });
  const { data: sourceTypes = [] } = useFeedSourceTypes();
  const { data, isLoading, error } = useFeed(filters);

  return (
    <>
      <Card>
        <CardTitle>Feed</CardTitle>
        <CardDescription className="mt-2">
          Chronological stream of extracted news items across all tracked sources.
        </CardDescription>
        <FeedFilters {...filters} sourceTypes={sourceTypes} onChange={setFilters} />
      </Card>

      {isLoading && <LoadingSkeleton lines={5} />}
      {error && <ErrorState message={(error as Error).message} />}

      {data && data.length === 0 && (
        <Card>
          <CardDescription>
            No items match. Try widening the range or lowering min score.
          </CardDescription>
        </Card>
      )}

      {data && data.length > 0 && (
        <>
          <Card>
            <p className="text-muted text-xs uppercase tracking-widest font-(family-name:--font-ui) mb-2">
              {data.length} items
            </p>
            {/* Desktop table */}
            <table className="hidden md:table w-full text-sm">
              <thead>
                <tr className="border-b-2 border-accent">
                  <Th width="110px">When</Th>
                  <Th width="140px">Source</Th>
                  <Th width="180px">Entity</Th>
                  <Th>Headline</Th>
                  <Th width="60px" center>Score</Th>
                </tr>
              </thead>
              <tbody>
                {data.map((i) => (
                  <FeedRowDesktop key={i.id} row={i} />
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <ul className="md:hidden flex flex-col divide-y divide-border">
              {data.map((i) => (
                <FeedRowMobile key={i.id} row={i} />
              ))}
            </ul>
          </Card>
        </>
      )}
    </>
  );
}

function Th({ children, width, center }: { children: React.ReactNode; width?: string; center?: boolean }) {
  return (
    <th
      style={width ? { width } : undefined}
      className={`py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui) ${center ? 'text-center' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function FeedRowDesktop({ row }: { row: FeedRowDto }) {
  return (
    <tr className="border-b border-border hover:bg-surface-hover align-top">
      <td className="py-3 px-3 text-muted text-xs whitespace-nowrap">{fmtLocalDate(row.publishedAt)}</td>
      <td className="py-3 px-3 text-muted text-xs">{row.sourceType}</td>
      <td className="py-3 px-3">{row.entityName ?? '—'}</td>
      <td className="py-3 px-3">
        <a href={row.sourceUrl} target="_blank" rel="noopener" className="text-accent hover:underline">
          {row.headline}
        </a>
        {row.summary && (
          <div className="text-muted text-[0.85rem] mt-1">{truncate(row.summary, 200)}</div>
        )}
        {row.whyItMatters && (
          <div className="text-[0.85rem] mt-1 text-accent">↳ {row.whyItMatters}</div>
        )}
        <ExpandableArticle content={row.content} contentFetchStatus={row.contentFetchStatus} />
      </td>
      <td className="py-3 px-3 text-center align-middle">
        <ScoreTag score={row.relevanceScore} />
      </td>
    </tr>
  );
}

function FeedRowMobile({ row }: { row: FeedRowDto }) {
  return (
    <li className="py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 text-[0.7rem] text-muted font-(family-name:--font-ui) uppercase tracking-wider">
            <span>{fmtLocalDate(row.publishedAt)}</span>
            <span>·</span>
            <span>{row.sourceType}</span>
            {row.entityName && (
              <>
                <span>·</span>
                <span>{row.entityName}</span>
              </>
            )}
          </div>
          <a href={row.sourceUrl} target="_blank" rel="noopener" className="text-accent hover:underline font-medium">
            {row.headline}
          </a>
          {row.summary && (
            <p className="text-muted text-sm mt-1">{truncate(row.summary, 160)}</p>
          )}
          {row.whyItMatters && (
            <p className="text-sm mt-1 text-accent">↳ {row.whyItMatters}</p>
          )}
          <ExpandableArticle content={row.content} contentFetchStatus={row.contentFetchStatus} />
        </div>
        <div className="flex-shrink-0"><ScoreTag score={row.relevanceScore} /></div>
      </div>
    </li>
  );
}
